import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TID } from "@/constants/testIds";

export const SHORTCUTS = [
  { keys: ["S"], alt: "/", label: "Search pages" },
  { keys: ["T"], label: "Create a new task" },
  { keys: ["H"], label: "Go to My Day (home)" },
  { keys: ["E"], label: "Whiteboard · By Editor view" },
  { keys: ["L"], label: "Whiteboard · List view" },
  { keys: ["B"], label: "Whiteboard · Board (Kanban) view" },
  { keys: ["C"], label: "Whiteboard · Calendar view" },
  { keys: ["W"], label: "Whiteboard · Weekly Grid view" },
  { keys: ["?"], label: "Show this list" },
  { keys: ["Ctrl", "K"], label: "Search pages (anywhere)" },
];

// Click an element once it appears (after a route change), give up after ~3s.
function clickWhenReady(selector, tries = 30) {
  const el = document.querySelector(selector);
  if (el) { el.click(); return; }
  if (tries > 0) setTimeout(() => clickWhenReady(selector, tries - 1), 100);
}

const isTyping = (e) => {
  const t = e.target;
  return t && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName));
};

export const KeyboardShortcuts = () => {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) return;
      if (document.querySelector('[role="dialog"]') && e.key !== "?") return;
      const k = e.key.toLowerCase();
      const views = { e: "board", l: "list", b: "kanban", c: "calendar", w: "weekly-grid" };
      if (k === "s" || k === "/") {
        e.preventDefault();
        window.dispatchEvent(new Event("lim:open-search"));
      } else if (k === "t") {
        e.preventDefault();
        const btn = document.querySelector(`[data-testid="${TID.addTaskBtn}"], [data-testid="my-tasks-add-task-btn"], [data-testid="my-day-new-task"]`);
        if (btn) btn.click();
        else { nav("/my-tasks"); clickWhenReady('[data-testid="my-tasks-add-task-btn"]'); }
      } else if (views[k]) {
        e.preventDefault();
        if (pathname !== "/") nav("/");
        clickWhenReady(`[data-testid="view-toggle-${views[k]}"]`);
      } else if (k === "h") {
        e.preventDefault();
        nav("/my-day");
      } else if (e.key === "?") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav, pathname]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-surface border-white/10 text-white max-w-md" data-testid="shortcuts-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Keyboard shortcuts</DialogTitle>
          <DialogDescription className="text-white/50">Work faster without the mouse. Shortcuts pause while you type.</DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-white/10">
          {SHORTCUTS.map((s) => (
            <li key={s.label} className="flex items-center justify-between py-2.5 text-sm">
              <span className="text-white/80">{s.label}</span>
              <span className="flex items-center gap-1">
                {s.keys.map((k) => <Kbd key={k}>{k}</Kbd>)}
                {s.alt && <><span className="text-white/40 text-xs mx-0.5">or</span><Kbd>{s.alt}</Kbd></>}
              </span>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};

const Kbd = ({ children }) => (
  <kbd className="min-w-[24px] h-6 px-1.5 grid place-items-center rounded border border-white/15 bg-surface2 text-xs font-mono text-white/80 shadow-[0_1px_0_rgba(0,0,0,0.08)]">{children}</kbd>
);
